import {
  type UpvotePayload,
  wasHandledNetworkError,
} from '@mentimeter/http-clients';
import { addBreadcrumb, captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import { postUpvote } from '../../../api/series';
import { useAppSelector } from '../../../redux-hooks';
import { voteKeySelector } from '../../../selectors';

interface SubmitUpvoteBody {
  voteKey: string;
  questionPublicKey: string;
  payload: UpvotePayload;
  partial?: boolean;
  voteId: string;
}

export const submitUpvote = async ({
  voteKey,
  questionPublicKey,
  partial = false,
  payload,
  voteId,
}: SubmitUpvoteBody) => {
  try {
    return await postUpvote({
      voteKey,
      questionPublicKey,
      payload,
      voteId,
    });
  } catch (err: any) {
    const reason = err?.response?.data?.reason;
    const code = err?.code;

    if (
      reason ||
      code === 'ECONNABORTED' ||
      code === 'ETIMEDOUT' ||
      code === 'ERR_NETWORK' ||
      err?.crossDomain
    ) {
      return;
    }

    if (err instanceof Error && !wasHandledNetworkError(err)) {
      // Only report unknown failures
      addBreadcrumb({
        message: 'submitUpvote error',
        data: {
          questionPublicKey,
          partial,
          payload,
          err,
        },
      });
      captureException(
        new MentiError('submit upvote failed', {
          cause: err,
          feature: 'slides',
          tags: {
            migrated: false,
          },
        }),
      );
      return;
    }

    return;
  }
};

export const useSubmitUpvote = (
  questionPublicKey: QuestionWithSlide['slidePublicKey'],
) => {
  const voteKey = useAppSelector(voteKeySelector);
  const questionType: QuestionWithSlide['type'] = 'open';

  return async (voteId: string) =>
    submitUpvote({
      voteId,
      voteKey,
      questionPublicKey,
      payload: {
        type: questionType,
      },
    });
};
